import { type RefObject, useEffect } from 'react';

/**
 * Focus trap for the Dialog primitive (R3). While `active`, keeps Tab / Shift+Tab
 * focus inside `ref`, moves initial focus into the container, and restores focus
 * to the previously-focused element when it deactivates.
 *
 * Hand-rolled (the operator approved a focus-trap util for R3; this is the
 * first-party one — no new dependency, no supply-chain surface, jsdom-friendly).
 * Deliberately does NOT filter by visibility (`offsetParent`) so it behaves
 * identically under jsdom, where layout is not computed.
 */
// `:not([tabindex="-1"])` on every type selector so an element deliberately taken
// out of the tab order (e.g. R13's listbox option buttons, driven by
// aria-activedescendant) is not cycled by Tab inside the dialog.
const FOCUSABLE = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));

    // Move focus into the dialog (first focusable, else the container itself).
    (focusables()[0] ?? node).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        node.focus();
        return;
      }
      const activeEl = document.activeElement;
      if (e.shiftKey && (activeEl === first || activeEl === node)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      // Restore focus to where it was before the dialog opened.
      previouslyFocused?.focus?.();
    };
  }, [active, ref]);
}
